// client/src/hooks/useCursos.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import useAlert from './useAlert';

const CURSOS_QUERY_KEY = 'cursos';

export const useCursos = (accountId, params = {}) =>
    useQuery({
        queryKey: [CURSOS_QUERY_KEY, accountId, params],
        queryFn: () => api.get('/cursos', { params: { account: accountId, ...params } }).then(r => r.data),
        enabled: !!accountId,
    });

export const useCurso = (cursoId) =>
    useQuery({
        queryKey: ['curso', cursoId],
        queryFn: () => api.get(`/cursos/${cursoId}`).then(r => r.data.data),
        enabled: !!cursoId,
    });

export const useMutateCursos = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidate = () => queryClient.invalidateQueries({ queryKey: [CURSOS_QUERY_KEY] });

    const getErrorMessage = (err, fallback) =>
        err.response?.data?.message || err.response?.data?.error || fallback;

    const createCurso = useMutation({
        mutationFn: (data) => api.post('/cursos', data),
        onSuccess: () => {
            invalidate();
            setAlert('Curso criado com sucesso.', 'success');
        },
        onError: (err) => setAlert(getErrorMessage(err, 'Erro ao criar curso.'), 'danger'),
    });

    const updateCurso = useMutation({
        mutationFn: ({ id, data }) => api.put(`/cursos/${id}`, data),
        onSuccess: () => {
            invalidate();
            setAlert('Curso atualizado com sucesso.', 'success');
        },
        onError: (err) => setAlert(getErrorMessage(err, 'Erro ao atualizar curso.'), 'danger'),
    });

    const deleteCurso = useMutation({
        mutationFn: (id) => api.delete(`/cursos/${id}`),
        onSuccess: () => {
            invalidate();
            setAlert('Curso excluído com sucesso.', 'success');
        },
        onError: (err) => setAlert(getErrorMessage(err, 'Erro ao excluir curso.'), 'danger'),
    });

    const uploadCursoImagem = useMutation({
        mutationFn: ({ cursoId, file }) => {
            const formData = new FormData();
            formData.append('imagem', file);
            return api.post(`/cursos/${cursoId}/imagens`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        },
        onSuccess: () => {
            invalidate();
            setAlert('Imagem enviada com sucesso.', 'success');
        },
        onError: (err) => setAlert(getErrorMessage(err, 'Erro ao enviar imagem.'), 'danger'),
    });

    const deleteCursoImagem = useMutation({
        mutationFn: ({ cursoId, imageId }) => api.delete(`/cursos/${cursoId}/imagens/${imageId}`),
        onSuccess: () => {
            invalidate();
            setAlert('Imagem removida com sucesso.', 'success');
        },
        onError: (err) => setAlert(getErrorMessage(err, 'Erro ao remover imagem.'), 'danger'),
    });

    // Régua de conteúdo (módulos/aulas) — sempre salva o array inteiro de
    // uma vez (mesmo padrão de HomeConfig.sections via SectionsManager.js);
    // o `ordem` de cada módulo/aula é recalculado no backend a partir da
    // posição no array, o drag-and-drop só reordena localmente antes de salvar.
    const atualizarCurriculo = useMutation({
        mutationFn: ({ cursoId, modulos }) => api.put(`/cursos/${cursoId}/curriculo`, { modulos }),
        onSuccess: () => {
            invalidate();
            setAlert('Régua de conteúdo salva com sucesso.', 'success');
        },
        onError: (err) => setAlert(getErrorMessage(err, 'Erro ao salvar régua de conteúdo.'), 'danger'),
    });

    const isPending = createCurso.isPending || updateCurso.isPending || deleteCurso.isPending ||
        uploadCursoImagem.isPending || deleteCursoImagem.isPending || atualizarCurriculo.isPending;

    return { createCurso, updateCurso, deleteCurso, uploadCursoImagem, deleteCursoImagem, atualizarCurriculo, isPending };
};

export default useCursos;
